import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UpdatePassword } from '../../redux/doctorSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Eye, EyeOff, X, Check, AlertTriangle } from 'lucide-react';

function PasswordUpdateModal({ isOpen, onClose, userId, userType = 'doctor' }) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { t } = useTranslation(['doctorpassword', 'common']);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({ password: '', confirmPassword: '' });
    const language = localStorage.getItem('i18nextLng');

    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.doctor);

    const validatePassword = (pwd) => {
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        if (!minLength) return t('doctorpassword.text1', { ns: 'doctorpassword' });
        if (!maxLength) return t('validation.password_too_long_25', { ns: 'common' });
        if (!hasUpperCase) return t('doctorpassword.text2', { ns: 'doctorpassword' });
        if (!hasLowerCase) return t('doctorpassword.text3', { ns: 'doctorpassword' });
        if (!hasNumber) return t('doctorpassword.text4', { ns: 'doctorpassword' });
        if (!hasSpecialChar) return t('doctorpassword.text5', { ns: 'doctorpassword' });
        return '';
    };

    const getPasswordStrength = (pwd) => {
        if (!pwd) return '';
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        // Password is strong if it meets all requirements
        if (minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
            return t('doctorpassword.strong');
        }
        return t('doctorpassword.weak');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation
        const passwordError = validatePassword(password);
        const confirmPasswordError =
            password !== confirmPassword
                ? t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                : '';

        if (passwordError || confirmPasswordError) {
            setErrors({
                password: passwordError,
                confirmPassword: confirmPasswordError,
            });
            return;
        }

        // SweetAlert confirmation BEFORE API hit
        const result = await Swal.fire({
            title: t('doctorpassword.confirm_title'),
            text: t('doctorpassword.confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('doctorpassword.yes'),
            cancelButtonText: t('doctorpassword.no'),
        });

        if (result.isConfirmed) {
            try {
                await dispatch(
                    UpdatePassword({
                        docId: userId,
                        body: { password, confirmPassword },
                    })
                ).unwrap();

                // Reset form
                setPassword('');
                setConfirmPassword('');
                setErrors({ password: '', confirmPassword: '' });

                // Success alert
                toast.success(t('doctorpassword.success_text'));

                // Close modal
                onClose();
            } catch (err) {
                // Error alert
                const errorMessage = language === 'en' 
                    ? err?.error?.response?.data?.message?.en 
                    : language === 'fr' 
                        ? err?.error?.response?.data?.message?.fr 
                        : t('doctorpassword.error_text');
                
                toast.warn(errorMessage || t('doctorpassword.error_text'));
            }
        }
    };

    const handleCancel = () => {
        setPassword('');
        setConfirmPassword('');
        setErrors({ password: '', confirmPassword: '' });
        setShowPassword(false);
        setShowConfirmPassword(false);
        onClose();
    };

    // Reset form when modal closes
    React.useEffect(() => {
        if (!isOpen) {
            setPassword('');
            setConfirmPassword('');
            setErrors({ password: '', confirmPassword: '' });
            setShowPassword(false);
            setShowConfirmPassword(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="password-update-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 relative" data-testid="password-update-modal-content">
                {/* Close Button */}
                <button
                    onClick={handleCancel}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 cursor-pointer"
                    disabled={loading}
                    data-testid="password-update-modal-close-button"
                >
                    <X className="h-5 w-5" data-testid="password-update-modal-close-icon" />
                </button>

                {/* Header */}
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent mb-6 pr-8" data-testid="password-update-modal-title">
                    {t('doctorpassword.heading', { ns: 'doctorpassword' })}
                </h2>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="password-update-form">
                    {/* New Password Field */}
                    <div className="relative" data-testid="password-update-new-password-field-container">
                        <label htmlFor="modal-password" className="block text-sm font-semibold text-gray-600 mb-2" data-testid="password-update-new-password-label">
                            {t('doctorpassword.new', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                id="modal-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => {
                                    const newPassword = e.target.value;
                                    
                                    // Enforce max length
                                    if (newPassword.length > 25) {
                                        return;
                                    }
                                    
                                    setPassword(newPassword);
                                    
                                    // Clear password error if exists
                                    if (errors.password) {
                                        setErrors({ ...errors, password: '' });
                                    }
                                    
                                    // Check confirm password match if confirm password has value
                                    if (confirmPassword && newPassword !== confirmPassword) {
                                        setErrors(prev => ({
                                            ...prev,
                                            confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                        }));
                                    } else if (confirmPassword && newPassword === confirmPassword) {
                                        setErrors(prev => ({
                                            ...prev,
                                            confirmPassword: ''
                                        }));
                                    }
                                }}
                                maxLength={25}
                                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                placeholder={t('doctorpassword.newtext', { ns: 'doctorpassword' })}
                                disabled={loading}
                                data-testid="password-update-new-password-input"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                                data-testid="password-update-new-password-toggle-button"
                            >
                                {showPassword ? (
                                    <Eye className="h-5 w-5" data-testid="password-update-new-password-eye-icon" />
                                ) : (
                                    <EyeOff className="h-5 w-5" data-testid="password-update-new-password-eyeoff-icon" />
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <p className="mt-2 text-xs text-red-500 font-medium" data-testid="password-update-new-password-error">{errors.password}</p>
                        )}
                        {password && !errors.password && (
                            <p className={`text-xs mt-1 flex items-center gap-1 ${
                                getPasswordStrength(password) === t('doctorpassword.strong')
                                    ? 'text-green-600'
                                    : 'text-amber-600'
                            }`} data-testid="password-update-strength-text">
                                {getPasswordStrength(password) === t('doctorpassword.strong')
                                    ? <Check className="w-3 h-3" data-testid="password-update-strength-strong-icon" />
                                    : <AlertTriangle className="w-3 h-3" data-testid="password-update-strength-weak-icon" />}
                                {t('doctorpassword.password_strength')} {getPasswordStrength(password)}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="relative" data-testid="password-update-confirm-password-field-container">
                        <label htmlFor="modal-confirm-password" className="block text-sm font-semibold text-gray-600 mb-2" data-testid="password-update-confirm-password-label">
                            {t('doctorpassword.confirm', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                id="modal-confirm-password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => {
                                    const newConfirmPassword = e.target.value;
                                    
                                    // Enforce max length
                                    if (newConfirmPassword.length > 25) {
                                        return;
                                    }
                                    
                                    setConfirmPassword(newConfirmPassword);
                                    
                                    // Clear error if exists
                                    if (errors.confirmPassword) {
                                        setErrors({ ...errors, confirmPassword: '' });
                                    }
                                    
                                    // Check if confirm password matches new password
                                    if (password && newConfirmPassword !== password) {
                                        setErrors(prev => ({
                                            ...prev,
                                            confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                        }));
                                    } else if (password && newConfirmPassword === password) {
                                        setErrors(prev => ({
                                            ...prev,
                                            confirmPassword: ''
                                        }));
                                    }
                                }}
                                maxLength={25}
                                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                                placeholder={t('doctorpassword.confirmtext', { ns: 'doctorpassword' })}
                                disabled={loading}
                                data-testid="password-update-confirm-password-input"
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={loading}
                                data-testid="password-update-confirm-password-toggle-button"
                            >
                                {showConfirmPassword ? (
                                    <Eye className="h-5 w-5" data-testid="password-update-confirm-password-eye-icon" />
                                ) : (
                                    <EyeOff className="h-5 w-5" data-testid="password-update-confirm-password-eyeoff-icon" />
                                )}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <p className="mt-2 text-xs text-red-500 font-medium" data-testid="password-update-confirm-password-error">{errors.confirmPassword}</p>
                        )}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end gap-4 pt-4" data-testid="password-update-actions-container">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                            disabled={loading}
                            data-testid="password-update-cancel-button"
                        >
                            {t('doctorpassword.cancel', { ns: 'doctorpassword' })}
                        </button>
                        <button
                            type="submit"
                            className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold relative overflow-hidden text-sm ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                            disabled={loading}
                            data-testid="password-update-submit-button"
                        >
                            {loading ? t('doctorpassword.updating', { ns: 'doctorpassword' }) : t('doctorpassword.update', { ns: 'doctorpassword' })}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default PasswordUpdateModal;

